import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, SlidersHorizontal } from "lucide-react";
import axios from "axios";
import ProductCard from "../components/ProductCard";
import { useLanguage } from "../contexts/LanguageContext";
import { useCart } from "../contexts/CartContext";

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || "https://lirkprint-backend.onrender.com").replace(/\/$/, "");
const API = `${BASE_URL}/api`;

export default function Shop() {
  const { t, lang } = useLanguage();
  const { addItem } = useCart();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("featured");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const categories = ["all", "sculpture", "geometric", "architecture", "functional"];
  const limit = 12;

  const fetchProducts = async (reset = false, pg = 1) => {
    setLoading(true);
    try {
      const params = {
        page: pg,
        limit,
        in_shop: true,
        ...(category !== "all" && { category }),
        ...(search && { search }),
        ...(sort && { sort })
      };
      const { data } = await axios.get(`${API}/products`, { params });
      const prods = data.products || [];

      setProducts(reset ? prods : prev => [...prev, ...prods]);
      setTotal(data.total);
      setHasMore((pg * limit) < data.total);
      setPage(pg);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchProducts(true, 1);
  }, [category, sort]); // eslint-disable-line

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchProducts(true, 1);
  };

  return (
    <div className="min-h-screen bg-background pt-28">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <span className="text-[11px] font-mono tracking-[0.3em] uppercase text-foreground/40 mb-3 block">
            {t("shop.subtitle")}
          </span>
          <h1 className="text-5xl font-heading font-bold tracking-tighter text-foreground">
            {t("shop.title")}
          </h1>
        </motion.div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-4 mb-10">
          {/* Search */}
          <form onSubmit={handleSearch} className="relative flex-1">
            <Search size={14} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/30" />
            <input
              data-testid="shop-search"
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={lang === "de" ? "Suchen..." : "Search..."}
              className="w-full pl-10 pr-4 py-3 bg-surface border border-border text-foreground text-sm rounded-sm placeholder:text-foreground/30 focus:outline-none focus:border-foreground/40 transition-colors"
            />
          </form>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={14} strokeWidth={1.5} className="text-foreground/40" />
            <select
              data-testid="shop-sort"
              value={sort}
              onChange={e => setSort(e.target.value)}
              className="bg-surface border border-border text-foreground text-sm px-3 py-3 rounded-sm focus:outline-none focus:border-foreground/40 transition-colors"
            >
              <option value="featured">{t("shop.sort.featured")}</option>
              <option value="price_asc">{t("shop.sort.price_asc")}</option>
              <option value="price_desc">{t("shop.sort.price_desc")}</option>
              <option value="newest">{t("shop.sort.newest")}</option>
            </select>
          </div>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map(cat => (
            <button
              key={cat}
              data-testid={`shop-cat-${cat}`}
              onClick={() => setCategory(cat)}
              className={`px-4 py-2 text-[11px] font-mono tracking-widest uppercase rounded-sm transition-all duration-300 ${
                category === cat
                  ? "bg-foreground text-background"
                  : "border border-border text-foreground/50 hover:border-foreground/40 hover:text-foreground"
              }`}
            >
              {t(`home.categories.${cat}`)}
            </button>
          ))}
        </div>

        {/* Results count */}
        <p className="text-xs font-mono text-foreground/30 mb-6">
          {total} {lang === "de" ? "Produkte" : "products"}
        </p>

        {/* Product Grid */}
        {loading && products.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-square bg-surface border border-border rounded-sm animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-foreground/30">
            <p className="text-lg font-heading">{t("shop.no_products")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {products.map((product, i) => (
              <ProductCard
                key={product.id}
                product={product}
                index={i}
                onAddToCart={addItem}
              />
            ))}
          </div>
        )}

        {/* Load more */}
        {hasMore && (
          <div className="flex justify-center mt-12">
            <button
              data-testid="load-more-btn"
              onClick={() => fetchProducts(false, page + 1)}
              disabled={loading}
              className="px-8 py-3 border border-border text-foreground/60 text-sm font-mono tracking-wider uppercase rounded-sm hover:border-foreground/50 hover:text-foreground transition-all duration-300 disabled:opacity-40"
            >
              {loading ? t("home.loading") : t("home.load_more")}
            </button>
          </div>
        )}
        <div className="pb-24" />
      </div>
    </div>
  );
}
